import { MAP_UI } from '../theme/mapViewUi';

/**
 * Shared Wi‑Fi RRM plan UI: marker styles (map), device roles (Configuration tab).
 */

export function rrmSerialKey(serial) {
  return (serial || '').toLowerCase();
}

export function isNodeDevice(device) {
  if (!device?.capabilities) return false;
  const model = (device.capabilities.model || '').toLowerCase();
  if (model.includes('nvidia') || model.includes('jetson') || model.includes('qrb5165')) {
    return false;
  }
  if (model.includes('juganucap') || model.includes('cap')) {
    return false;
  }
  if (model.includes('eap102') || model.includes('edgecore')) {
    return false;
  }
  if (device.compatible !== 'jnet2') return false;
  return true;
}

/** jnet2 Juganu CAP (eligible for Public WiFi assign with tree_id). */
export function isJnet2CapDevice(device) {
  return device?.compatible === 'jnet2' && isCapDevice(device);
}

export function ssidBandNeedsCapTreeSelect(band) {
  const normalized = String(band || '2.4GHz,5GHz').toLowerCase();
  return normalized.includes('5ghz') || normalized.includes('6ghz');
}

export function radioProfileNeedsCapTreeSelect(band) {
  const normalized = String(band || '').toLowerCase();
  if (normalized === 'all' || normalized.includes('5ghz') || normalized.includes('6ghz')) {
    return true;
  }
  return false;
}

export const DEFAULT_CAP_TREE_ID = '5GHz_1';

/** Build device_tree_ids for jnet2 CAPs in a group when SSID band needs a CAP slot. */
export function buildCapDeviceTreeIdsForGroup(devices, serials, ssidBand, treeId = DEFAULT_CAP_TREE_ID) {
  if (!ssidBandNeedsCapTreeSelect(ssidBand)) return undefined;
  const serialSet = new Set((serials || []).map((s) => String(s).toLowerCase()));
  const caps = (devices || []).filter(
    (d) => serialSet.has((d.serial_number || '').toLowerCase()) && isJnet2CapDevice(d),
  );
  if (caps.length === 0) return undefined;
  return Object.fromEntries(
    caps.map((d) => [(d.serial_number || '').toLowerCase(), treeId]),
  );
}

export function capUpperTreeIdsFromConfig(configuration) {
  const treemgmt = Array.isArray(configuration?.treemgmt) ? configuration.treemgmt : [];
  const fromTreemgmt = treemgmt
    .map((entry) => String(entry?.tree_id || '').trim())
    .filter((treeId) => treeId.toLowerCase().startsWith('5ghz'));
  if (fromTreemgmt.length > 0) {
    return [...new Set(fromTreemgmt)];
  }
  const radios = Array.isArray(configuration?.radio) ? configuration.radio : [];
  return [...new Set(
    radios
      .map((entry) => String(entry?.radio || '').trim())
      .filter((radioName) => radioName.toLowerCase().startsWith('5ghz')),
  )];
}

/** Juganu CAP (root AP) — always shown on Public WiFi map layers. */
export function isCapDevice(device) {
  if (!device?.capabilities) return false;
  const model = (device.capabilities.model || '').toLowerCase();
  if (model.includes('nvidia') || model.includes('jetson') || model.includes('qrb5165')) {
    return false;
  }
  return model.includes('juganucap') || model.includes('cap');
}

/** Parse device.gps into finite lat/lng, or null when unusable for map plotting. */
export function deviceMapCoordinates(device) {
  const gps = device?.gps || null;
  if (!gps || gps.lat == null || gps.lng == null) return null;
  const lat = typeof gps.lat === 'number' ? gps.lat : parseFloat(gps.lat);
  const lng = typeof gps.lng === 'number' ? gps.lng : parseFloat(gps.lng);
  if (!Number.isFinite(lat) || !Number.isFinite(lng) || (lat === 0 && lng === 0)) return null;
  return { lat, lng };
}

function isValidSsid(ssid) {
  if (!ssid || typeof ssid !== 'string') return false;
  const trimmed = ssid.trim();
  const lower = trimmed.toLowerCase();
  return lower !== 'n/a' && lower !== '-' && trimmed !== '';
}

/** NODE with active Public WiFi AP (any band) — matches PublicWifiExpansion logic. */
export function hasPublicWifi(device) {
  if (!isNodeDevice(device)) return false;
  if (!device?.public_wifi) return false;
  if (device.public_wifi.enabled === false) return false;

  const topLevelSsid = device.public_wifi.ssid || device.public_wifi.SSID;
  const hasValidTopLevelSsid = topLevelSsid && isValidSsid(topLevelSsid);

  const interfaces = device.public_wifi.interfaces || device.public_wifi.Interfaces;
  let hasValidInterfaceSsid = false;
  let hasApModeWithSsid = false;

  if (interfaces && Array.isArray(interfaces) && interfaces.length > 0) {
    hasValidInterfaceSsid = interfaces.some((iface) => {
      const mode = (iface?.Mode || iface?.mode || '').toString().trim().toLowerCase();
      const ssid = iface.SSID || iface.ssid;
      if (mode === 'ap' && isValidSsid(ssid)) {
        hasApModeWithSsid = true;
        return true;
      }
      return false;
    });
  }

  if (!hasValidTopLevelSsid && !hasValidInterfaceSsid) return false;
  if (!hasApModeWithSsid && !hasValidTopLevelSsid) return false;
  return true;
}

/** NODE with true 5 GHz Public WiFi AP (excludes 6 GHz-only on 5GHz_0). */
export function has5GHzPublicWifi(device) {
  if (!isNodeDevice(device)) return false;
  if (!device?.public_wifi) return false;
  if (device.public_wifi.enabled === false) return false;
  const interfaces = device.public_wifi.interfaces || device.public_wifi.Interfaces;
  if (!interfaces || !Array.isArray(interfaces) || interfaces.length === 0) return false;
  return interfaces.some((iface) => {
    const mode = (iface?.Mode || iface?.mode || '').toString().trim().toLowerCase();
    const ssid = iface.SSID || iface.ssid;
    const band = (iface?.Band || iface?.band || iface?.Radio || iface?.radio || '').toString().toLowerCase();
    if (band.includes('6ghz') || band === '3' || band === '6') return false;
    return mode === 'ap' && isValidSsid(ssid) && (band.includes('5ghz') || band.includes('5g') || band === '5' || band === '2');
  });
}

/** NODE with 6 GHz Public WiFi AP (band code 3 / 6 GHz on 5GHz_0 slot). */
export function has6GHzPublicWifi(device) {
  if (!isNodeDevice(device)) return false;
  if (!device?.public_wifi) return false;
  if (device.public_wifi.enabled === false) return false;
  const interfaces = device.public_wifi.interfaces || device.public_wifi.Interfaces;
  if (!interfaces || !Array.isArray(interfaces) || interfaces.length === 0) return false;
  return interfaces.some((iface) => {
    const mode = (iface?.Mode || iface?.mode || '').toString().trim().toLowerCase();
    const ssid = iface.SSID || iface.ssid;
    const band = (iface?.Band || iface?.band || iface?.Radio || iface?.radio || '').toString().toLowerCase();
    return mode === 'ap' && isValidSsid(ssid) && (band.includes('6ghz') || band === '3' || band === '6');
  });
}

/**
 * Index serials from last GET /public-wifi/rrm-plan payload.
 * planEntryMap: lowercase serial → plan entry
 */
export function buildRrmPlanIndex(rrmData) {
  if (!rrmData) {
    return {
      hasData: false,
      planEntryMap: {},
      planSerialSet: new Set(),
      excludedSerialSet: new Set(),
      rrmMapSerialSet: new Set(),
    };
  }
  const planEntryMap = Object.fromEntries(
    (rrmData.plan_5ghz || []).map((e) => [rrmSerialKey(e.serial), e]),
  );
  const planSerialSet = new Set(Object.keys(planEntryMap));
  const excludedSerialSet = new Set(
    (rrmData.excluded || []).map((e) => rrmSerialKey(e.serial)).filter(Boolean),
  );
  const candSerials = (rrmData.candidates || []).map((c) => rrmSerialKey(c.serial)).filter(Boolean);
  const rrmMapSerialSet = new Set([...planSerialSet, ...excludedSerialSet, ...candSerials]);
  return {
    hasData: true,
    planEntryMap,
    planSerialSet,
    excludedSerialSet,
    rrmMapSerialSet,
  };
}

/** Non-excluded map marker: blue P/W, green N, red W. */
export function getRrmMapMarkerStyle(serial, planEntryMap, publicWifiAnySerials) {
  const s = rrmSerialKey(serial);
  const inPlan = !!planEntryMap[s];
  const hasPW = publicWifiAnySerials.has(s);
  if (inPlan) {
    return { color: MAP_UI.rrmPlanMarker, label: hasPW ? 'W' : 'P' };
  }
  if (hasPW) {
    return { color: MAP_UI.deviceOffline, label: 'W' };
  }
  return { color: MAP_UI.deviceOnline, label: 'N' };
}

/** Excluded critical parent: blue X or red !! (5 GHz PW conflict). */
export function getRrmExcludedMarkerStyle(serial, excludedSerialSet, publicWifi5GHzSerials) {
  const s = rrmSerialKey(serial);
  if (!excludedSerialSet.has(s)) return null;
  const conflict = publicWifi5GHzSerials.has(s);
  return {
    color: conflict ? MAP_UI.deviceOfflineBorder : MAP_UI.rrmPlanMarker,
    label: conflict ? '!!' : 'X',
  };
}

export function createRrmMarkerDiv(color, label, { size = 26, borderWidth = '2px' } = {}) {
  const iconStr = String(label ?? '');
  const fontSize = iconStr.length > 1 ? '8px' : '10px';
  const div = document.createElement('div');
  div.style.cssText = `
    display: flex;
    align-items: center;
    justify-content: center;
    width: ${size}px;
    height: ${size}px;
    background-color: ${color};
    border: ${borderWidth} solid #fff;
    border-radius: 50%;
    color: #fff;
    font-size: ${fontSize};
    font-weight: bold;
    font-family: Arial, sans-serif;
    cursor: pointer;
    box-shadow: 0 2px 6px rgba(0,0,0,0.3);
  `;
  div.textContent = label;
  return div;
}

/**
 * Device row role for Configuration → Devices (5 GHz Public WiFi plan).
 * @returns {'planned'|'optional'|'critical'|'conflict'|null}
 */
export function getRrmDevicePlanRole(serial, { excludedSerialSet, planSerialSet, deviceHas5GHzPublicWifi }) {
  if (!excludedSerialSet || !planSerialSet) return null;
  const s = rrmSerialKey(serial);
  if (excludedSerialSet.has(s)) {
    return deviceHas5GHzPublicWifi ? 'conflict' : 'critical';
  }
  if (planSerialSet.has(s)) return 'planned';
  return 'optional';
}

/** Show non-blocking API warnings (e.g. critical tree-parent advisories on SSID assign). */
export function showApiWarnings(enqueueSnackbar, res) {
  const warnings = res?.warnings;
  if (!Array.isArray(warnings) || warnings.length === 0) return;
  warnings.forEach((w) => {
    if (typeof w === 'string' && w.trim()) {
      enqueueSnackbar(w, { variant: 'warning', autoHideDuration: 10000 });
    }
  });
}
